import React, { useMemo } from 'react';
import { AnnotationType } from '../../api/protosCompiled/annotation/annotation_pb';
import { ShapeEditHandlesProps } from './types';

export const ShapeEditHandles: React.FC<ShapeEditHandlesProps> = ({
  annotation,
  isSelected,
  transform,
  liveEditingTransform,
  liveEditingGeometry,
  onResizeStart,
  onResizeDrag,
  onResizeEnd,
  onRotateStart,
  onRotateDrag,
  onRotateEnd,
}) => {
  // Get bounding box and handles based on annotation type - use live editing geometry for real-time updates
  const { boundingBox, handles, rotationHandle } = useMemo(() => {
    if (!isSelected) {
      return { boundingBox: null, handles: [], rotationHandle: null };
    }

    // Use live editing geometry if available, otherwise fall back to annotation geometry
    const effectiveGeometry = liveEditingGeometry || annotation.geometry;

    const pos = {
      x: parseFloat(effectiveGeometry?.x || '0'),
      y: parseFloat(effectiveGeometry?.y || '0'),
    };

    switch (annotation.type) {
      case AnnotationType.TEXT:
      case AnnotationType.RECTANGLE: {
        const width = parseFloat(effectiveGeometry?.width || '100');
        const height = parseFloat(effectiveGeometry?.height || '50');

        return {
          boundingBox: {
            x: pos.x,
            y: pos.y,
            width,
            height,
          },
          handles: [
            { id: 'nw', x: pos.x, y: pos.y, cursor: 'nw-resize' },
            { id: 'n', x: pos.x + width / 2, y: pos.y, cursor: 'n-resize' },
            { id: 'ne', x: pos.x + width, y: pos.y, cursor: 'ne-resize' },
            { id: 'e', x: pos.x + width, y: pos.y + height / 2, cursor: 'e-resize' },
            { id: 'se', x: pos.x + width, y: pos.y + height, cursor: 'se-resize' },
            { id: 's', x: pos.x + width / 2, y: pos.y + height, cursor: 's-resize' },
            { id: 'sw', x: pos.x, y: pos.y + height, cursor: 'sw-resize' },
            { id: 'w', x: pos.x, y: pos.y + height / 2, cursor: 'w-resize' },
          ],
          rotationHandle: {
            x: pos.x + width / 2,
            y: pos.y - 40,
          },
        };
      }

      case AnnotationType.HYPERLINK: {
        const width = parseFloat(effectiveGeometry?.width || '24');
        const height = parseFloat(effectiveGeometry?.height || '24');

        return {
          boundingBox: {
            x: pos.x,
            y: pos.y,
            width,
            height,
          },
          handles: [
            { id: 'nw', x: pos.x, y: pos.y, cursor: 'nw-resize' },
            { id: 'n', x: pos.x + width / 2, y: pos.y, cursor: 'n-resize' },
            { id: 'ne', x: pos.x + width, y: pos.y, cursor: 'ne-resize' },
            { id: 'e', x: pos.x + width, y: pos.y + height / 2, cursor: 'e-resize' },
            { id: 'se', x: pos.x + width, y: pos.y + height, cursor: 'se-resize' },
            { id: 's', x: pos.x + width / 2, y: pos.y + height, cursor: 's-resize' },
            { id: 'sw', x: pos.x, y: pos.y + height, cursor: 'sw-resize' },
            { id: 'w', x: pos.x, y: pos.y + height / 2, cursor: 'w-resize' },
          ],
          rotationHandle: null, // No rotation for hyperlinks
        };
      }

      case AnnotationType.CIRCLE: {
        const cx = parseFloat(effectiveGeometry?.cx || pos.x.toString());
        const cy = parseFloat(effectiveGeometry?.cy || pos.y.toString());
        const r = parseFloat(effectiveGeometry?.r || '25');

        return {
          boundingBox: {
            x: cx - r,
            y: cy - r,
            width: r * 2,
            height: r * 2,
          },
          handles: [
            { id: 'nw', x: cx - r, y: cy - r, cursor: 'nw-resize' },
            { id: 'n', x: cx, y: cy - r, cursor: 'n-resize' },
            { id: 'ne', x: cx + r, y: cy - r, cursor: 'ne-resize' },
            { id: 'e', x: cx + r, y: cy, cursor: 'e-resize' },
            { id: 'se', x: cx + r, y: cy + r, cursor: 'se-resize' },
            { id: 's', x: cx, y: cy + r, cursor: 's-resize' },
            { id: 'sw', x: cx - r, y: cy + r, cursor: 'sw-resize' },
            { id: 'w', x: cx - r, y: cy, cursor: 'w-resize' },
          ],
          rotationHandle: {
            x: cx,
            y: cy - r - 20,
          },
        };
      }

      case AnnotationType.LINE:
      case AnnotationType.ARROW: {
        const x1 = parseFloat(effectiveGeometry?.x1 || pos.x.toString());
        const y1 = parseFloat(effectiveGeometry?.y1 || pos.y.toString());
        const x2 = parseFloat(effectiveGeometry?.x2 || (pos.x + 100).toString());
        const y2 = parseFloat(effectiveGeometry?.y2 || pos.y.toString());

        const minX = Math.min(x1, x2);
        const maxX = Math.max(x1, x2);
        const minY = Math.min(y1, y2);
        const maxY = Math.max(y1, y2);

        return {
          boundingBox: {
            x: minX,
            y: minY,
            width: maxX - minX || 10, // Ensure minimum width for visibility
            height: maxY - minY || 10, // Ensure minimum height for visibility
          },
          handles: [
            { id: 'start', x: x1, y: y1, cursor: 'move' },
            { id: 'end', x: x2, y: y2, cursor: 'move' },
          ],
          rotationHandle: null, // No rotation handle for lines and arrows
        };
      }

      case AnnotationType.HIGHLIGHTER: {
        const width = parseFloat(effectiveGeometry?.width || '100');
        const height = parseFloat(effectiveGeometry?.height || '20');

        return {
          boundingBox: {
            x: pos.x,
            y: pos.y,
            width,
            height,
          },
          handles: [
            { id: 'nw', x: pos.x, y: pos.y, cursor: 'nw-resize' },
            { id: 'n', x: pos.x + width / 2, y: pos.y, cursor: 'n-resize' },
            { id: 'ne', x: pos.x + width, y: pos.y, cursor: 'ne-resize' },
            { id: 'e', x: pos.x + width, y: pos.y + height / 2, cursor: 'e-resize' },
            { id: 'se', x: pos.x + width, y: pos.y + height, cursor: 'se-resize' },
            { id: 's', x: pos.x + width / 2, y: pos.y + height, cursor: 's-resize' },
            { id: 'sw', x: pos.x, y: pos.y + height, cursor: 'sw-resize' },
            { id: 'w', x: pos.x, y: pos.y + height / 2, cursor: 'w-resize' },
          ],
          rotationHandle: {
            x: pos.x + width / 2,
            y: pos.y - 40,
          },
        };
      }

      case AnnotationType.ELLIPSE: {
        const cx = parseFloat(effectiveGeometry?.cx || pos.x.toString());
        const cy = parseFloat(effectiveGeometry?.cy || pos.y.toString());
        const rx = parseFloat(effectiveGeometry?.rx || '40');
        const ry = parseFloat(effectiveGeometry?.ry || '25');

        return {
          boundingBox: {
            x: cx - rx,
            y: cy - ry,
            width: rx * 2,
            height: ry * 2,
          },
          handles: [
            { id: 'nw', x: cx - rx, y: cy - ry, cursor: 'nw-resize' },
            { id: 'n', x: cx, y: cy - ry, cursor: 'n-resize' },
            { id: 'ne', x: cx + rx, y: cy - ry, cursor: 'ne-resize' },
            { id: 'e', x: cx + rx, y: cy, cursor: 'e-resize' },
            { id: 'se', x: cx + rx, y: cy + ry, cursor: 'se-resize' },
            { id: 's', x: cx, y: cy + ry, cursor: 's-resize' },
            { id: 'sw', x: cx - rx, y: cy + ry, cursor: 'sw-resize' },
            { id: 'w', x: cx - rx, y: cy, cursor: 'w-resize' },
          ],
          rotationHandle: {
            x: cx,
            y: cy - ry - 20,
          },
        };
      }

      case AnnotationType.POLYLINE: {
        const points = effectiveGeometry?.points;
        if (!points) return { boundingBox: null, handles: [], rotationHandle: null };

        const coords = points.split(' ').map((p: string) => {
          const [x, y] = p.split(',').map(Number);
          return { x, y };
        });

        if (coords.length === 0) return { boundingBox: null, handles: [], rotationHandle: null };

        const minX = Math.min(...coords.map((p: { x: number; y: number }) => p.x));
        const maxX = Math.max(...coords.map((p: { x: number; y: number }) => p.x));
        const minY = Math.min(...coords.map((p: { x: number; y: number }) => p.y));
        const maxY = Math.max(...coords.map((p: { x: number; y: number }) => p.y));

        return {
          boundingBox: {
            x: minX,
            y: minY,
            width: maxX - minX,
            height: maxY - minY,
          },
          handles: coords.map((coord: { x: number; y: number }, index: number) => ({
            id: `point-${index}`,
            x: coord.x,
            y: coord.y,
            cursor: 'move',
          })),
          rotationHandle: {
            x: (minX + maxX) / 2,
            y: minY - 40,
          },
        };
      }
      case AnnotationType.POLYGON: {
        const points = effectiveGeometry?.points;
        if (!points) return { boundingBox: null, handles: [], rotationHandle: null };

        const coords = points.split(' ').map((p: string) => {
          const [x, y] = p.split(',').map(Number);
          return { x, y };
        });

        if (coords.length === 0) return { boundingBox: null, handles: [], rotationHandle: null };

        const minX = Math.min(...coords.map((p: { x: number; y: number }) => p.x));
        const maxX = Math.max(...coords.map((p: { x: number; y: number }) => p.x));
        const minY = Math.min(...coords.map((p: { x: number; y: number }) => p.y));
        const maxY = Math.max(...coords.map((p: { x: number; y: number }) => p.y));

        // Create handles for vertices
        const vertexHandles = coords.map((coord: { x: number; y: number }, index: number) => ({
          id: `point-${index}`,
          x: coord.x,
          y: coord.y,
          cursor: 'move',
        }));

        // Create handles for edge midpoints
        const midpointHandles = [];
        for (let i = 0; i < coords.length; i++) {
          const current = coords[i];
          const next = coords[(i + 1) % coords.length]; // Wrap around for polygon

          const midX = (current.x + next.x) / 2;
          const midY = (current.y + next.y) / 2;

          midpointHandles.push({
            id: `midpoint-${i}`,
            x: midX,
            y: midY,
            cursor: 'move',
          });
        }

        return {
          boundingBox: {
            x: minX,
            y: minY,
            width: maxX - minX,
            height: maxY - minY,
          },
          handles: [...vertexHandles, ...midpointHandles],
          rotationHandle: {
            x: (minX + maxX) / 2,
            y: minY - 40,
          },
        };
      }

      default:
        return { boundingBox: null, handles: [], rotationHandle: null };
    }
  }, [annotation, isSelected, liveEditingGeometry]);

  if (!isSelected || !boundingBox) return null;

  const handleSize = 8;
  const rotationHandleSize = 6;

  return (
    <g className="shape-edit-handles">
      {/* Bounding box */}

      {/* Resize handles */}
      {handles.map((handle: { id: string; x: number; y: number; cursor: string }) => (
        <g key={handle.id}>
          <rect
            x={handle.x - handleSize / 2}
            y={handle.y - handleSize / 2}
            width={handleSize}
            height={handleSize}
            fill="#ffffff"
            stroke="#0066cc"
            strokeWidth="2"
            transform={transform}
            style={{ cursor: handle.cursor }}
            onMouseDown={e => {
              e.preventDefault();
              e.stopPropagation();
              // Gate drag-select while interacting with handles
              try {
                const store = (
                  require('../annotations/annotationStore') as any
                ).useAnnotationStore?.getState?.();
                store?.setIsDraggingShape?.(true);
              } catch {}
              // Convert mouse to SVG coords
              const svg = (e.currentTarget.ownerSVGElement as SVGSVGElement) || undefined;
              const toSvg = (evt: MouseEvent): { x: number; y: number } => {
                const pt = svg!.createSVGPoint();
                pt.x = evt.clientX;
                pt.y = evt.clientY;
                const m = svg!.getScreenCTM();
                const inv = m && (m as any).inverse ? (m as any).inverse() : null;
                const res = inv ? pt.matrixTransform(inv) : (pt as any);
                return { x: res.x, y: res.y };
              };

              const start = toSvg(e as unknown as MouseEvent);
              onResizeStart?.(handle.id, start);

              const onMoveHandler = (ev: MouseEvent) => {
                onResizeDrag?.(handle.id, toSvg(ev));
              };
              const onUpHandler = () => {
                try {
                  const store = (
                    require('../annotations/annotationStore') as any
                  ).useAnnotationStore?.getState?.();
                  store?.setIsDraggingShape?.(false);
                } catch {}
                onResizeEnd?.();
                window.removeEventListener('mousemove', onMoveHandler);
                window.removeEventListener('mouseup', onUpHandler);
              };
              window.addEventListener('mousemove', onMoveHandler);
              window.addEventListener('mouseup', onUpHandler);
            }}
            onMouseEnter={e => {
              (e.currentTarget as SVGRectElement).style.cursor = handle.cursor as any;
            }}
          />
          <circle
            cx={handle.x}
            cy={handle.y}
            r={handleSize / 2 + 2}
            fill="none"
            stroke="#0066cc"
            strokeWidth="1"
            opacity="0.5"
            transform={transform}
          />
        </g>
      ))}

      {/* Rotation handle - rotates with annotation transform */}
      {rotationHandle && (
        <g>
          <line
            x1={boundingBox.x + boundingBox.width / 2}
            y1={boundingBox.y}
            x2={rotationHandle.x}
            y2={rotationHandle.y}
            stroke="#0066cc"
            strokeWidth="1"
            strokeDasharray="3,3"
            opacity="0.6"
            transform={transform}
          />

          {/* Rotation angle display - updates in real-time during rotation */}
          <text
            x={rotationHandle.x}
            y={rotationHandle.y - 15}
            textAnchor="middle"
            fontSize="12"
            fill="#0066cc"
            fontWeight="bold"
            opacity="0.8"
            transform={transform}
          >
            {liveEditingTransform?.rotation
              ? `${Math.round(Number(liveEditingTransform.rotation))}°`
              : annotation.transform?.rotation
                ? `${Math.round(Number(annotation.transform.rotation))}°`
                : '0°'}
          </text>
          <circle
            cx={rotationHandle.x}
            cy={rotationHandle.y}
            r={rotationHandleSize}
            fill="#ffffff"
            stroke="#0066cc"
            strokeWidth="2"
            transform={transform}
            style={{ cursor: 'grab' }}
            onMouseEnter={e => {
              (e.currentTarget as SVGCircleElement).style.cursor = 'grab';
            }}
            onMouseLeave={e => {
              (e.currentTarget as SVGCircleElement).style.cursor = 'grab';
            }}
            onMouseDown={e => {
              (e.currentTarget as SVGCircleElement).style.cursor = 'grabbing';
              e.preventDefault();
              e.stopPropagation();
              try {
                const store = (
                  require('../annotations/annotationStore') as any
                ).useAnnotationStore?.getState?.();
                store?.setIsDraggingShape?.(true);
              } catch {}
              const svg = (e.currentTarget.ownerSVGElement as SVGSVGElement) || undefined;
              const toSvg = (evt: MouseEvent): { x: number; y: number } => {
                const pt = svg!.createSVGPoint();
                pt.x = evt.clientX;
                pt.y = evt.clientY;
                const m = svg!.getScreenCTM();
                const inv = m && (m as any).inverse ? (m as any).inverse() : null;
                const res = inv ? pt.matrixTransform(inv) : (pt as any);
                return { x: res.x, y: res.y };
              };
              onRotateStart?.(toSvg(e as unknown as MouseEvent));

              const onMoveHandler = (ev: MouseEvent) => onRotateDrag?.(toSvg(ev));
              const onUpHandler = () => {
                try {
                  const store = (
                    require('../annotations/annotationStore') as any
                  ).useAnnotationStore?.getState?.();
                  store?.setIsDraggingShape?.(false);
                } catch {}
                onRotateEnd?.();
                window.removeEventListener('mousemove', onMoveHandler);
                window.removeEventListener('mouseup', onUpHandler);
              };
              window.addEventListener('mousemove', onMoveHandler);
              window.addEventListener('mouseup', onUpHandler);
            }}
          />
        </g>
      )}
    </g>
  );
};
